import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { animate, motion, useInView } from 'framer-motion';
import './Home.css';
import HeroSection from '../components/HeroSection';
import ProductCarousel from '../components/ProductCarousel';
import ChampionTimeline from '../components/ChampionTimeline';
import ZiruiSpotlight from '../components/ZiruiSpotlight';
import Seo from '../components/Seo';
import getApiBase from '../utils/apiBase';
import { teamData } from './teamData';
import { useI18n } from '../i18n/I18nContext';

const API_BASE = getApiBase();

const TRUST_ITEM_KEYS = [
  'home.trust.national',
  'home.trust.asiaPacific',
  'home.trust.first',
  'home.trust.team',
];

const StatCard = ({ value, suffix = '', label, prefix = '', isNumeric = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(isNumeric ? 0 : value);

  useEffect(() => {
    if (!isNumeric || !inView) return undefined;
    const controls = animate(0, value, {
      duration: 1.4,
      ease: 'easeOut',
      onUpdate: (latest) => setDisplay(Math.round(latest)),
    });
    return () => controls.stop();
  }, [inView, value, isNumeric]);

  return (
    <motion.div
      ref={ref}
      className="stat-card"
      initial={{ y: 16, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ duration: 0.6 }}
    >
      <div className="stat-card-value">
        {prefix}
        {isNumeric ? display : value}
        {suffix}
      </div>
      <div className="stat-card-label">{label}</div>
    </motion.div>
  );
};

const Home = () => {
  const { t } = useI18n();
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://ych-yoyo.com';

  const [productCount, setProductCount] = useState(21);

  useEffect(() => {
    let isMounted = true;
    fetch(`${API_BASE}/api/products?page=1&per_page=1&include_custom=0`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (isMounted && Number.isFinite(Number(data?.total))) {
          setProductCount(Number(data.total));
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'YCH-YOYO',
    url: siteUrl,
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'YCH-YOYO',
    url: siteUrl,
    logo: `${siteUrl}/images/ych_yoyo_logo.jpg`,
    description: t('home.seoDescription'),
    founder: {
      '@type': 'Person',
      name: 'Cheng Hao Yi',
      sameAs: 'https://www.instagram.com/madman8517/',
      award: [
        `${t('timeline.title.1')} (2015–2019)`,
        `${t('timeline.title.3')} (${t('timeline.note.first')})`,
      ],
    },
    sameAs: ['https://www.instagram.com/madman8517/'],
  };

  const homePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `YCH-YOYO | ${t('home.seoTitle')}`,
    url: `${siteUrl}/`,
    description: t('home.seoDescription'),
  };

  return (
    <div className="home-container">
      <Seo
        title={t('home.seoTitle')}
        description={t('home.seoDescription')}
        path="/"
        image="/images/blitzball_main.jpg"
        structuredData={[websiteSchema, organizationSchema, homePageSchema]}
      />

      <HeroSection />

      <section id="trust-bar" className="trust-bar" aria-label={t('home.credentials')}>
        <div className="trust-bar-inner">
          {TRUST_ITEM_KEYS.map((key, idx) => (
            <React.Fragment key={key}>
              <span className="trust-bar-item">{t(key)}</span>
              {idx < TRUST_ITEM_KEYS.length - 1 && (
                <span aria-hidden className="trust-bar-divider">·</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="manifesto" aria-labelledby="manifesto-title">
        <div className="manifesto-grid">
          <motion.div
            className="manifesto-copy"
            initial={{ y: 16, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7 }}
          >
            <p className="manifesto-eyebrow">{t('home.storyEyebrow')}</p>
            <h2 id="manifesto-title" className="manifesto-title">
              {t('home.storyTitle')}
            </h2>
            <p className="manifesto-body">
              {t('home.storyBody')}
            </p>
          </motion.div>

          <div className="stats-grid" role="list" aria-label={t('home.atAGlance')}>
            <StatCard value={productCount} label={t('home.stat.products')} />
            <StatCard value={9} label={t('home.stat.athletes')} />
            <StatCard value={5} suffix="×" label={t('home.stat.titles')} />
            <StatCard isNumeric={false} value={t('home.stat.firstValue')} label={t('home.stat.first')} />
          </div>
        </div>
      </section>

      <ProductCarousel />

      <ChampionTimeline />

      <ZiruiSpotlight />

      <section className="final-cta" aria-labelledby="final-cta-title">
        <div className="final-cta-inner">
          <motion.div
            className="final-cta-team"
            initial={{ y: 16, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
          >
            <div className="final-cta-avatars" aria-hidden>
              {teamData
                .filter((m) => [1, 8, 2].includes(m.id))
                .map((member) => (
                  <img
                    key={member.id}
                    src={member.imageUrl}
                    alt=""
                    className="final-cta-avatar"
                    loading="lazy"
                    decoding="async"
                  />
                ))}
            </div>
            <Link to="/team" data-analytics-target="home_team" className="final-cta-team-link">
              {t('home.meetTeam')} <span aria-hidden>→</span>
            </Link>
          </motion.div>

          <motion.div
            className="final-cta-headline-wrap"
            initial={{ y: 16, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.05 }}
          >
            <p className="final-cta-eyebrow">{t('home.ready')}</p>
            <h2 id="final-cta-title" className="final-cta-headline">
              {t('home.join')}
            </h2>
            <p className="final-cta-sub">
              {t('home.finalSub')}
            </p>
          </motion.div>

          <motion.div
            className="final-cta-buttons"
            initial={{ y: 16, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <Link to="/store" data-analytics-target="home_store" className="final-cta-btn final-cta-btn--primary">
              {t('home.shop')}
            </Link>
            <Link
              to="/guides/blitzball-vs-windball"
              className="final-cta-btn final-cta-btn--secondary"
            >
              {t('home.guide')}
            </Link>
          </motion.div>

          <p className="final-cta-foot">
            {t('home.worldwide')} ·{' '}
            <Link to="/shipping" className="final-cta-foot-link">
              {t('home.seeShipping')}
            </Link>
            {' · '}
            <Link to="/stories" className="final-cta-foot-link">
              {t('home.readStory')}
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
